"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { getCurrentUser } from "@/app/actions/auth";

/**
 * 전역 로그인 상태 — [locale] layout 이 서버에서 getCurrentUser() 를 부르던 자리를
 * 대신한다.
 *
 * 왜 클라이언트인가: layout 이 쿠키를 읽으면 [locale] 아래 모든 페이지가 동적 렌더로
 * 떨어져 ISR/CDN 캐시가 전부 무력화된다(prod 전 페이지 `private, no-store`). 캐시된
 * HTML 에는 사용자별 데이터가 절대 들어가면 안 되므로, 로그인 의존 UI 는 전부 여기서
 * 하이드레이션 후에 결정한다.
 *
 * 정확성 규칙
 * - 판정은 브라우저 클라이언트의 `auth.getClaims()`(JWT 서명 검증)로 한다.
 *   `getSession()` 만으로는 판정하지 않는다(쿠키 값을 검증 없이 믿게 된다).
 * - claims 가 없으면 즉시 anonymous — 서버 호출 0회(비로그인 방문자 비용 0).
 * - claims 가 있으면 프로필은 `getCurrentUser()` 서버 액션(쿠키로 서버가 재검증 —
 *   단일 출처)에서 받는다. null 이면 anonymous.
 * - 로그인/로그아웃은 대부분 전체 새로고침(window.location)으로 끝나지만, 서버 액션
 *   로그아웃 + router.replace 처럼 브라우저 클라이언트가 이벤트를 내지 않는 경로가 있어
 *   pathname 변경·탭 복귀 때마다 getClaims(로컬 검증)로 다시 확인하고, 사용자 id 가
 *   바뀐 경우에만 프로필을 다시 가져온다.
 */

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
  /** customer_profiles.preferred_language (getCurrentUser 와 동일하게 기본 "ko") */
  preferred_language: string | null;
  terms_accepted_at: string | null;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** 프로필을 강제로 다시 가져온다 (약관 동의·프로필 변경 직후 등). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthState = { status: AuthStatus; user: AuthUser | null };

const ANONYMOUS: AuthState = { status: "anonymous", user: null };

/** 로컬 JWT 검증으로 현재 세션의 사용자 id 를 얻는다. 없으면 null. */
async function readVerifiedUserId(): Promise<string | null | "unknown"> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.auth.getClaims();
    if (error || !data?.claims?.sub) return null;
    return data.claims.sub;
  } catch (error) {
    // JWKS 조회 실패·auth lock 경합 등 일시 장애 — 판정 불가. 호출부가 서버에
    // 물어본다(서버가 쿠키로 재검증하므로 결과는 여전히 정확하다).
    console.warn("[AuthProvider] getClaims failed:", error);
    return "unknown";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
  });

  // 마지막으로 프로필을 확정한 사용자 id (anonymous 면 null). 렌더에 쓰지 않으므로 ref.
  const resolvedUserIdRef = useRef<string | null | undefined>(undefined);
  // 늦게 도착한 이전 요청 결과가 최신 상태를 덮어쓰지 않도록 요청 순번으로 거른다.
  const seqRef = useRef(0);

  const resolve = useCallback(async (force: boolean) => {
    const seq = ++seqRef.current;
    const claimedId = await readVerifiedUserId();
    if (seq !== seqRef.current) return;

    if (claimedId === null) {
      resolvedUserIdRef.current = null;
      setState(ANONYMOUS);
      return;
    }

    if (
      !force &&
      claimedId !== "unknown" &&
      resolvedUserIdRef.current === claimedId
    ) {
      return; // 같은 사용자 — 프로필 재조회 불필요
    }

    let profile: Awaited<ReturnType<typeof getCurrentUser>> = null;
    try {
      profile = await getCurrentUser();
    } catch (error) {
      // 서버 액션 POST 가 페이지 전환·WebView 수명주기로 끊긴 경우 등.
      // 종전 layout 과 같게 anonymous 로 두고, 다음 경로 변경·탭 복귀 때 다시 확인한다
      // (resolvedUserIdRef 를 비워 두므로 재조회가 일어난다).
      console.warn("[AuthProvider] getCurrentUser failed:", error);
    }
    if (seq !== seqRef.current) return;

    if (!profile) {
      resolvedUserIdRef.current = undefined;
      setState(ANONYMOUS);
      return;
    }

    resolvedUserIdRef.current = profile.id;
    setState({
      status: "authenticated",
      user: {
        id: profile.id,
        email: profile.email,
        full_name: profile.full_name ?? null,
        preferred_language: profile.preferred_language ?? null,
        terms_accepted_at: profile.terms_accepted_at ?? null,
      },
    });
  }, []);

  // 최초 마운트 + 경로 변경마다 로컬 재검증 (사용자 id 가 같으면 서버 호출 없음).
  useEffect(() => {
    // resolve 의 setState 는 모두 await(getClaims) 뒤라 동기 cascading render 가 아니다
    // — 외부 시스템(세션 쿠키) 동기화의 전형. 규칙이 useCallback 내부의 await 를 못 본다.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void resolve(false);
  }, [pathname, resolve]);

  useEffect(() => {
    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        void resolve(false);
      } else if (event === "USER_UPDATED") {
        void resolve(true);
      } else if (event === "TOKEN_REFRESHED") {
        // 토큰 갱신은 같은 사용자면 무시 — 다른 사용자로 바뀐 경우만 재확인.
        if ((session?.user?.id ?? null) !== (resolvedUserIdRef.current ?? null)) {
          void resolve(false);
        }
      }
    });

    // 다른 탭에서 로그아웃/로그인(쿠키는 탭 간 공유)한 뒤 이 탭으로 돌아온 경우.
    const handleVisible = () => {
      if (document.visibilityState === "visible") void resolve(false);
    };
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [resolve]);

  const refresh = useCallback(() => resolve(true), [resolve]);

  const value = useMemo<AuthContextValue>(
    () => ({ status: state.status, user: state.user, refresh }),
    [state, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
